import React, { useEffect, useMemo, useState } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useTranslation } from 'react-i18next';
import "./home.css";
import apiClient from '../../../shared/utils/apiConfig';
import { showErrorToast } from '../../utils/toastHelper';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const AnimatedNumber = ({ value = 0, durationMs = 800 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf = null;
    const start = performance.now();
    const startVal = 0;
    const endVal = Number(value) || 0;
    const animate = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(startVal + (endVal - startVal) * eased));
      if (t < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [value, durationMs]);
  return <>{display}</>;
};

const Home = () => {
  const { t, i18n } = useTranslation("global");
  const { setTitle } = useTitle();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [role, setRole] = useState(null);
  
  useEffect(() => {
    setTitle("لوحة التحكم");
  }, [setTitle]);
  
  // Detect role (admin | store | pharmacy | others)
  useEffect(() => {
    const detectRole = () => {
      try {
        const adm = sessionStorage.getItem('adminkhadamat');
        if (adm) return 'admin';
      } catch {}
      try {
        const store = sessionStorage.getItem('storekhadamat');
        if (store) {
          const parsed = JSON.parse(store);
          if (parsed?.role === 'pharmacy' || parsed?.role === 'pharmacy_branch') return 'pharmacy';
          if (parsed?.role === 'store') return 'store';
        }
      } catch {}
      try {
        const clinic = sessionStorage.getItem('clinicKhadamat');
        if (clinic) {
          const parsed = JSON.parse(clinic);
          if (parsed?.role === 'clinic') return 'clinic';
        }
      } catch {}
      try {
        const doctor = sessionStorage.getItem('doctorKhadamat');
        if (doctor) {
          const parsed = JSON.parse(doctor);
          if (parsed?.role === 'doctor') return 'doctor';
        }
      } catch {}
      return null;
    };
    setRole(detectRole());
  }, []);

  // Fetch statistics based on role
  useEffect(() => {
    const fetchStats = async () => {
      if (!role) return;
      const endpoint =
        role === 'admin'
          ? '/admin/statistics'
          : (role === 'store' || role === 'pharmacy')
          ? 'store/statistics'
          : role === 'clinic'
          ? '/clinics_dash/clinic_home_statistics'
          : role === 'doctor'
          ? '/doctor_dash/doctor_home_statistics'
          : null;
      if (!endpoint) return;
      try {
        setLoading(true);
        const res = await apiClient.get(endpoint);
        const payload = res?.data ?? res;
        const dataNode = payload?.data ?? payload?.result ?? payload;
        setStats(dataNode || null);
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'فشل في جلب الإحصائيات';
        showErrorToast(message);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [role]);

  const adminCards = useMemo(() => {
    if (!stats || !role || role !== 'admin') return [];
    const s = stats || {};
    const list = [];
    if (s.stores) {
      list.push(
        { title: 'المتاجر - التجار', value: s.stores.vendors ?? 0, color: '#184194' },
        { title: 'المتاجر - الصيدليات', value: s.stores.pharmacies ?? 0, color: '#0ea5e9' },
        { title: 'المتاجر النشطة', value: s.stores.active ?? 0, color: '#16a34a' },
        { title: 'المتاجر المحظورة', value: s.stores.banned ?? 0, color: '#ef4444' },
      );
    }
    if (typeof s.products_count !== 'undefined') {
      list.push({ title: 'عدد المنتجات', value: s.products_count ?? 0, color: '#8b5cf6' });
    }
    if (s.orders) {
      list.push(
        { title: 'طلبات اليوم', value: s.orders.today ?? 0, color: '#0ea5e9' },
        { title: 'طلبات الشهر', value: s.orders.month ?? 0, color: '#0284c7' },
        { title: 'إجمالي الطلبات', value: s.orders.total ?? 0, color: '#0369a1' },
        { title: 'قيد الانتظار', value: s.orders.pending ?? 0, color: '#f59e0b' },
        { title: 'ملغاة', value: s.orders.cancelled ?? 0, color: '#ef4444' },
        { title: 'مكتملة', value: s.orders.completed ?? 0, color: '#16a34a' },
        { title: 'دفع عند الاستلام', value: s.orders.cod ?? 0, color: '#64748b' },
        { title: 'دفع أونلاين', value: s.orders.online ?? 0, color: '#22c55e' },
      );
    }
    if (typeof s.customers_count !== 'undefined') {
      list.push({ title: 'عدد العملاء', value: s.customers_count ?? 0, color: '#1f2937' });
    }
    if (s.revenue) {
      list.push(
        { title: 'إيراد اليوم', value: s.revenue.today ?? 0, color: '#16a34a' },
        { title: 'إيراد الشهر', value: s.revenue.month ?? 0, color: '#22c55e' },
        { title: 'إجمالي الإيراد', value: s.revenue.total ?? 0, color: '#15803d' },
      );
    }
    if (s.clinics) {
      list.push({ title: 'العيادات', value: s.clinics.total ?? 0, color: '#0ea5e9' });
    }
    if (s.doctors) {
      list.push(
        { title: 'الأطباء - الإجمالي', value: s.doctors.total ?? 0, color: '#184194' },
        { title: 'الأطباء - نشط', value: s.doctors.active ?? 0, color: '#16a34a' },
        { title: 'الأطباء - محظور', value: s.doctors.banned ?? 0, color: '#ef4444' },
        { title: 'مرتبطين بعيادات', value: s.doctors.linked_to_clinics ?? 0, color: '#8b5cf6' },
      );
    }
    if (s.delivery) {
      list.push(
        { title: 'الدليفري - الإجمالي', value: s.delivery.total ?? 0, color: '#0ea5e9' },
        { title: 'الدليفري - نشط', value: s.delivery.active ?? 0, color: '#16a34a' },
        { title: 'الدليفري - محظور', value: s.delivery.banned ?? 0, color: '#ef4444' },
      );
    }
    return list;
  }, [stats, role]);

  const parseNumber = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number') return v;
    const n = Number(String(v).replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const adminCharts = useMemo(() => {
    if (!stats || role !== 'admin') return null;
    const s = stats;
    const orders = s.orders || {};
    const revenue = s.revenue || {};
    const stores = s.stores || {};
    const doctors = s.doctors || {};

    const ordersData = [
      { name: 'اليوم', value: parseNumber(orders.today), color: '#0ea5e9' },
      { name: 'الشهر', value: parseNumber(orders.month), color: '#184194' },
      { name: 'الإجمالي', value: parseNumber(orders.total), color: '#0369a1' },
      { name: 'معلق', value: parseNumber(orders.pending), color: '#f59e0b' },
      { name: 'ملغي', value: parseNumber(orders.cancelled), color: '#ef4444' },
      { name: 'مكتمل', value: parseNumber(orders.completed), color: '#16a34a' },
      { name: 'كاش', value: parseNumber(orders.cod), color: '#64748b' },
      { name: 'أونلاين', value: parseNumber(orders.online), color: '#22c55e' }
    ];

    const revenueData = [
      { name: 'اليوم', value: parseNumber(revenue.today), color: '#16a34a' },
      { name: 'الشهر', value: parseNumber(revenue.month), color: '#22c55e' },
      { name: 'الإجمالي', value: parseNumber(revenue.total), color: '#15803d' }
    ];

    const storesPie = [
      { name: 'تجار', value: parseNumber(stores.vendors), color: '#184194' },
      { name: 'صيدليات', value: parseNumber(stores.pharmacies), color: '#0ea5e9' },
      { name: 'نشط', value: parseNumber(stores.active), color: '#16a34a' },
      { name: 'محظور', value: parseNumber(stores.banned), color: '#ef4444' }
    ].filter(d => d.value > 0);

    const doctorsPie = [
      { name: 'إجمالي', value: parseNumber(doctors.total), color: '#184194' },
      { name: 'نشط', value: parseNumber(doctors.active), color: '#16a34a' },
      { name: 'محظور', value: parseNumber(doctors.banned), color: '#ef4444' },
      { name: 'مرتبطين بعيادات', value: parseNumber(doctors.linked_to_clinics), color: '#8b5cf6' }
    ].filter(d => d.value > 0);

    return { ordersData, revenueData, storesPie, doctorsPie };
  }, [stats, role]);

  const genericCards = useMemo(() => {
    if (!stats || !role || (role !== 'store' && role !== 'pharmacy' && role !== 'clinic' && role !== 'doctor')) return [];
    const s = stats;
    const translateToken = (t) => {
      const map = {
        doctor: 'الطبيب',
        doctors: 'الأطباء',
        clinic: 'العيادة',
        clinics: 'العيادات',
        accepted: 'مقبولة',
        rejected: 'مرفوضة',
        last: 'الأخيرة',
        reservations: 'الحجوزات',
        reservation: 'الحجز',
        count: 'عدد',
        price: 'الإيراد',
        total: 'الإجمالي',
        today: 'اليوم',
        month: 'الشهر',
        pending: 'قيد الانتظار',
        cancelled: 'ملغاة',
        completed: 'مكتملة',
        active: 'نشط',
        banned: 'محظور',
      };
      return map[t] || t;
    };
    const translateKey = (k) => {
      // مفاتيح خاصة للطبيب
      const exact = {
        doctor_last_reservations_count: 'عدد الحجوزات الأخيرة',
        doctor_last_reservations_price: 'إجمالي أسعار الحجوزات الأخيرة',
        doctor_clinics_accepted: 'عدد العيادات المقبولة',
        doctor_clinics_rejected: 'عدد العيادات المرفوضة',
      };
      if (exact[k]) return exact[k];
      // تحويل عام: استبدال "_" بمسافة وترجمة الكلمات الشائعة
      return k.split('_').map(translateToken).join(' ');
    };
    const flatten = (obj, prefix = '') => {
      const out = [];
      Object.entries(obj || {}).forEach(([k, v]) => {
        const rawLabel = prefix ? `${prefix} - ${k}` : k;
        const label = translateKey(rawLabel);
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          out.push(...flatten(v, label));
        } else if (typeof v === 'number' || typeof v === 'string') {
          out.push({ title: label, value: v });
        }
      });
      return out;
    };
    return flatten(s).map((it, idx) => ({
      ...it,
      color: ['#184194','#0ea5e9','#16a34a','#ef4444','#8b5cf6','#f59e0b','#22c55e','#1f2937'][idx % 8]
    }));
  }, [stats, role]);

  const storeCharts = useMemo(() => {
    if (!stats || !role || (role !== 'store' && role !== 'pharmacy')) return null;
    const s = stats || {};
    const orders = s.orders || {};
    const revenue = s.revenue || {};
    const products = s.products || {};

    const ordersData = [
      { name: 'اليوم', value: parseNumber(orders.today), color: '#0ea5e9' },
      { name: 'الشهر', value: parseNumber(orders.month), color: '#184194' },
      { name: 'الإجمالي', value: parseNumber(orders.total), color: '#0369a1' },
    ];

    const revenueData = [
      { name: 'اليوم', value: parseNumber(revenue.today), color: '#16a34a' },
      { name: 'الشهر', value: parseNumber(revenue.month), color: '#22c55e' },
      { name: 'الإجمالي', value: parseNumber(revenue.total), color: '#15803d' },
    ];

    const productsPie = [
      { name: 'إجمالي', value: parseNumber(products.total), color: '#184194' },
      { name: 'نشط', value: parseNumber(products.active), color: '#16a34a' },
      { name: 'غير نشط', value: parseNumber(products.inactive), color: '#ef4444' },
    ].filter(d => d.value > 0);

    return { ordersData, revenueData, productsPie };
  }, [stats, role]);
  
  return (
      <div className="main_content">
      <div className="dashboard-container">
        {(!role || (!stats && !loading)) && (
          <div className="row mb-5">
            <div className="col-12 text-center">
              <img 
                src="/logokhadamat.png" 
                alt="خدمات منطقتي" 
                style={{ height: '120px', width: 'auto' }}
              />
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
          </div>
        )}

        {!loading && stats && role === 'admin' && (
          <div className="row g-3">
            {adminCharts && (
              <>
                <div className="col-12 col-lg-6">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <h6 className="mb-3" style={{ color: '#1f2937' }}>ملخص الطلبات</h6>
                      <div style={{ width: '100%', height: 260 }}>
                        <ResponsiveContainer>
                          <BarChart data={adminCharts.ordersData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value">
                              {adminCharts.ordersData.map((entry, index) => (
                                <Cell key={`ord-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <h6 className="mb-3" style={{ color: '#1f2937' }}>الإيرادات</h6>
                      <div style={{ width: '100%', height: 260 }}>
                        <ResponsiveContainer>
                          <BarChart data={adminCharts.revenueData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value">
                              {adminCharts.revenueData.map((entry, index) => (
                                <Cell key={`rev-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <h6 className="mb-3" style={{ color: '#1f2937' }}>المتاجر</h6>
                      <div style={{ width: '100%', height: 260 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Tooltip />
                            <Legend formatter={(value) => (
                              <span style={{ marginInlineStart: 8, marginLeft: 8 }}>{value}</span>
                            )} />
                            <Pie data={adminCharts.storesPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={4}>
                              {adminCharts.storesPie.map((entry, index) => (
                                <Cell key={`st-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <h6 className="mb-3" style={{ color: '#1f2937' }}>الأطباء</h6>
                      <div style={{ width: '100%', height: 260 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Tooltip />
                            <Legend formatter={(value) => (
                              <span style={{ marginInlineStart: 8, marginLeft: 8 }}>{value}</span>
                            )} />
                            <Pie data={adminCharts.doctorsPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={4}>
                              {adminCharts.doctorsPie.map((entry, index) => (
                                <Cell key={`dr-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {adminCards.map((card, idx) => (
              <div key={idx} className="col-12 col-sm-6 col-lg-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column align-items-center justify-content-center text-center">
                    <div style={{ color: card.color, fontWeight: 700, fontSize: '1.75rem' }}>
                      <AnimatedNumber value={card.value} />
                    </div>
                    <div className="mt-2" style={{ color: '#1f2937', fontWeight: 600 }}>
                      {card.title}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && stats && (role === 'store' || role === 'pharmacy') && storeCharts && (
          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <h6 className="mb-3" style={{ color: '#1f2937' }}>ملخص الطلبات</h6>
                  <div style={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={storeCharts.ordersData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value">
                          {storeCharts.ordersData.map((entry, index) => (
                            <Cell key={`s-ord-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <h6 className="mb-3" style={{ color: '#1f2937' }}>الإيرادات</h6>
                  <div style={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={storeCharts.revenueData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value">
                          {storeCharts.revenueData.map((entry, index) => (
                            <Cell key={`s-rev-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <h6 className="mb-3" style={{ color: '#1f2937' }}>المنتجات</h6>
                  <div style={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Tooltip />
                        <Legend formatter={(value) => (
                          <span style={{ marginInlineStart: 8, marginLeft: 8 }}>{value}</span>
                        )} />
                        <Pie data={storeCharts.productsPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={4}>
                          {storeCharts.productsPie.map((entry, index) => (
                            <Cell key={`s-pr-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {!loading && stats && (role === 'clinic' || role === 'doctor') && genericCards.length > 0 && (
          <div className="row g-3">
            {genericCards.map((card, idx) => (
              <div key={idx} className="col-12 col-sm-6 col-lg-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column align-items-center justify-content-center text-center">
                    <div style={{ color: card.color, fontWeight: 700, fontSize: '1.5rem' }}>
                      {card.value}
                    </div>
                    <div className="mt-2" style={{ color: '#1f2937', fontWeight: 600, wordBreak: 'break-word' }}>
                      {card.title}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        </div>
      </div>
  );
};

export default Home;
